import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import type { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { toast } from '@/store/toast'

import { setupApi } from './api'

/**
 * A portal's page wants a code it texted to the person (or first the phone
 * number to text it to). They type it here; the worker puts it into the
 * page's own box and presses its Verify button. The live picture stays below
 * for anything this cannot reach.
 */
export function PhoneCodeEntry({ portalKey, name }: { portalKey: string; name: string }) {
  const [code, setCode] = useState('')
  const [askPhone, setAskPhone] = useState(false)
  const profile = useQuery({ queryKey: ['profile'], queryFn: profileApi.get, staleTime: 5 * 60_000 })
  const [phone, setPhone] = useState<string | null>(null)
  const phoneValue = phone ?? profile.data?.contact?.phoneNumber ?? ''

  const sendCode = useMutation({
    mutationFn: () => setupApi.enterCode(portalKey, code),
    onSuccess: () => {
      setCode('')
      toast.success('Code entered', `We put it into ${name} and pressed Verify.`)
    },
    onError: (error: ApiError) => toast.error('That did not go through', error.message),
  })
  const sendPhone = useMutation({
    mutationFn: () => setupApi.enterPhone(portalKey, phoneValue),
    onSuccess: () => {
      setAskPhone(false)
      toast.success('Phone number entered', `${name} should text you a code now.`)
    },
    onError: (error: ApiError) => toast.error('That did not go through', error.message),
  })

  return (
    <div className="space-y-3 rounded-field border border-line bg-surface p-3 sm:p-4">
      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (code.length >= 4) sendCode.mutate()
        }}
      >
        <TextField
          label={`Code ${name} texted to your phone`}
          appearance="box"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 10))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          className="text-center font-mono text-2xl tracking-[0.4em]"
        />
        <Button type="submit" className="h-11 w-full sm:w-auto" disabled={code.length < 4} loading={sendCode.isPending}>
          Enter code
        </Button>
      </form>

      {askPhone ? (
        <form
          className="space-y-2 border-t border-line pt-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (phoneValue.trim()) sendPhone.mutate()
          }}
        >
          <TextField
            label="Phone number to text the code to"
            appearance="box"
            type="tel"
            autoComplete="tel"
            value={phoneValue}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+91 98765 43210"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="secondary" className="h-11 sm:h-9" disabled={!phoneValue.trim()} loading={sendPhone.isPending}>
              Send me the code
            </Button>
            <Button type="button" variant="ghost" className="h-11 sm:h-9" onClick={() => setAskPhone(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <button type="button" className="tap text-[13px] font-medium text-brand hover:underline" onClick={() => setAskPhone(true)}>
          No code yet? {name} may want your phone number first
        </button>
      )}
    </div>
  )
}
